import { EmptyState, ErrorState, Loading, useToast } from "@uzverse/ui";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { MemoryItem } from "../api/types.js";
import { ErrorNote, useCommonT, useErrorText } from "../components/common.js";
import { useSession } from "../state/session.js";
import { useLoad } from "../state/useLoad.js";

function MemoryRow({
  item,
  onCorrect,
  onDelete,
}: {
  item: MemoryItem;
  onCorrect: (id: string, text: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}) {
  const { t } = useTranslation("web");
  const [mode, setMode] = useState<"view" | "edit" | "confirm">("view");
  const [text, setText] = useState(item.text);
  const inputId = `memory-edit-${item.id}`;
  return (
    <li className="card">
      <span className="chip">{t(`memory.kind.${item.kind}`)}</span>
      {mode === "edit" ? (
        <form
          className="stack"
          onSubmit={(e) => {
            e.preventDefault();
            void onCorrect(item.id, text.trim()).then(() => setMode("view"));
          }}
        >
          <label className="field" htmlFor={inputId}>
            <span>{t("memory.editLabel")}</span>
            <textarea
              id={inputId}
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={3}
              required
            />
          </label>
          <div className="row">
            <button type="submit" className="btn btn-primary">
              {t("memory.saveCorrection")}
            </button>
            <button type="button" className="btn" onClick={() => setMode("view")}>
              {t("actions.cancel", { ns: "common" })}
            </button>
          </div>
        </form>
      ) : (
        <>
          <p>{item.text}</p>
          {mode === "confirm" ? (
            <div role="alertdialog" aria-label={t("memory.confirmDelete")} className="stack">
              <p>{t("memory.confirmDelete")}</p>
              <div className="row">
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => void onDelete(item.id)}
                >
                  {t("memory.confirmYes")}
                </button>
                <button type="button" className="btn" onClick={() => setMode("view")}>
                  {t("actions.cancel", { ns: "common" })}
                </button>
              </div>
            </div>
          ) : (
            <div className="row">
              <button type="button" className="btn" onClick={() => setMode("edit")}>
                {t("memory.edit")}
              </button>
              <button type="button" className="btn btn-quiet" onClick={() => setMode("confirm")}>
                {t("memory.delete")}
              </button>
            </div>
          )}
        </>
      )}
    </li>
  );
}

export function MemoryPage() {
  const { t } = useTranslation("web");
  const errorText = useErrorText();
  const tc = useCommonT();
  const toast = useToast();
  const { services } = useSession();
  const memory = useLoad(() => services.api.listMemory());
  const [error, setError] = useState<string | null>(null);

  async function guarded(action: () => Promise<void>) {
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(errorText(err));
      throw err;
    }
  }

  const onCorrect = (id: string, text: string) =>
    guarded(async () => {
      const updated = await services.api.correctMemory(id, text);
      memory.setData((prev) => prev?.map((m) => (m.id === id ? updated : m)) ?? prev);
    });
  const onDelete = (id: string) =>
    guarded(async () => {
      await services.api.deleteMemory(id);
      memory.setData((prev) => prev?.filter((m) => m.id !== id) ?? prev);
      toast.show(t("memory.deleted"));
    }).catch(() => undefined);

  return (
    <section aria-labelledby="memory-title" className="stack">
      <h1 id="memory-title">{t("memory.title")}</h1>
      <p className="muted">{t("memory.intro")}</p>
      <ErrorNote message={error} />
      {memory.error ? (
        <ErrorState t={tc} message={errorText(memory.error)} onRetry={memory.reload} />
      ) : !memory.data ? (
        <Loading t={tc} />
      ) : memory.data.length === 0 ? (
        <EmptyState t={tc} title={t("memory.empty")} />
      ) : null}
      <ul className="plain stack">
        {memory.data?.map((item) => (
          <MemoryRow
            key={item.id}
            item={item}
            onCorrect={(id, text) => onCorrect(id, text).catch(() => undefined)}
            onDelete={onDelete}
          />
        ))}
      </ul>
    </section>
  );
}
